'use client';

/**
 * Command Center Header
 * Includes global search, navigation, notifications, workspace switcher, and common actions
 */

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCommandCenter } from '@/lib/command-center/CommandCenterContext';
import { useWorkspace } from '@/lib/archimate/WorkspaceContext';
import GlobalSearch from './GlobalSearch';
import NotificationsPanel from './NotificationsPanel';

const NAV_ITEMS = [
  { href: '/command-center', label: 'Dashboard', exact: true },
  { href: '/command-center/entities3', label: 'Entities' },
  { href: '/command-center/tech-radar', label: 'Tech Radar' },
  { href: '/command-center/incidents', label: 'Incidents' },
  { href: '/command-center/risks', label: 'Risks' },
  { href: '/command-center/team', label: 'Team' },
];

const MORE_NAV_ITEMS = [
  { href: '/command-center/tech-debt', label: 'Tech Debt' },
  { href: '/command-center/decisions', label: 'Decisions' },
  { href: '/command-center/slos', label: 'SLOs' },
  { href: '/command-center/migrations-tracker', label: 'Migrations' },
  { href: '/command-center/teams', label: 'Teams' },
  { href: '/command-center/capacity', label: 'Capacity' },
  { href: '/command-center/vendors', label: 'Vendors' },
  { href: '/command-center/dependencies', label: 'Dependencies' },
  { href: '/command-center/compliance', label: 'Compliance' },
  { href: '/command-center/costs', label: 'Costs' },
];

export default function CommandCenterHeader() {
  const pathname = usePathname();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [workspaceSwitcherOpen, setWorkspaceSwitcherOpen] = useState(false);

  // Get entities and assessments from context for notifications
  const { entities, assessments, setWorkspaceId } = useCommandCenter();
  const { currentWorkspace, workspaces, switchWorkspace, createNewWorkspace } = useWorkspace();

  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const handleSelectWorkspace = async (id: string) => {
    await switchWorkspace(id);
    setWorkspaceId(id);
    setWorkspaceSwitcherOpen(false);
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;
    const newWs = await createNewWorkspace({ name: newWorkspaceName.trim(), type: 'local' });
    if (newWs) {
      setWorkspaceId(newWs.id);
    }
    setNewWorkspaceName('');
    setShowCreateForm(false);
    setWorkspaceSwitcherOpen(false);
  };

  // Handle Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (href: string, exact = false) => {
    if (exact) return pathname === href;
    return pathname.startsWith(href);
  };

  const handleNotificationCount = useCallback((count: number) => {
    setNotificationCount(count);
  }, []);

  return (
    <>
      <header className="bg-surface/50 border-b border-border-muted sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Logo / Title + Workspace Switcher */}
            <div className="flex items-center gap-3">
              <Link href="/command-center" className="flex items-center gap-2">
                <span className="text-xl">🎛️</span>
                <span className="text-foreground font-semibold hidden sm:inline">Command Center</span>
              </Link>

              {/* Workspace Switcher */}
              {currentWorkspace && (
                <div className="relative">
                  <button
                    onClick={() => setWorkspaceSwitcherOpen(!workspaceSwitcherOpen)}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm text-foreground-secondary hover:text-foreground hover:bg-cream/5 transition-colors"
                  >
                    <span className="text-foreground-tertiary">/</span>
                    <span className="max-w-[150px] truncate">{currentWorkspace.name}</span>
                    <svg className="w-3.5 h-3.5 text-foreground-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {workspaceSwitcherOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setWorkspaceSwitcherOpen(false)}
                      />
                      <div className="absolute top-full left-0 mt-1 w-64 bg-background border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                        <div className="p-2 border-b border-border-muted">
                          <div className="text-xs text-foreground-tertiary uppercase tracking-wide px-2 py-1">
                            Switch Workspace
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto p-1">
                          {workspaces.map((ws) => (
                            <button
                              key={ws.id}
                              onClick={() => handleSelectWorkspace(ws.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                ws.id === currentWorkspace.id
                                  ? 'bg-accent-muted text-accent'
                                  : 'text-foreground-secondary hover:bg-cream/5 hover:text-foreground'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {ws.id === currentWorkspace.id && (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                <span className={ws.id === currentWorkspace.id ? '' : 'ml-6'}>{ws.name}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="p-2 border-t border-border-muted">
                          {showCreateForm ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={newWorkspaceName}
                                onChange={(e) => setNewWorkspaceName(e.target.value)}
                                placeholder="Workspace name..."
                                className="w-full bg-background-dark border border-border rounded px-2 py-1.5 text-sm text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace()}
                              />
                              <div className="flex gap-1">
                                <button
                                  onClick={handleCreateWorkspace}
                                  disabled={!newWorkspaceName.trim()}
                                  className="flex-1 bg-accent text-accent-foreground text-sm px-2 py-1 rounded font-medium hover:bg-accent-hover disabled:opacity-50"
                                >
                                  Create
                                </button>
                                <button
                                  onClick={() => {
                                    setShowCreateForm(false);
                                    setNewWorkspaceName('');
                                  }}
                                  className="px-2 py-1 text-foreground-tertiary text-sm hover:text-foreground"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowCreateForm(true)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground-tertiary hover:text-foreground hover:bg-cream/5 rounded-lg transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              New Workspace
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="hidden sm:flex items-center gap-1">
              {NAV_ITEMS.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    isActive(item.href, item.exact)
                      ? 'bg-accent-muted text-accent'
                      : 'text-foreground-secondary hover:text-foreground hover:bg-cream/5'
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              {/* More Menu */}
              <div className="relative">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${
                    MORE_NAV_ITEMS.some(item => isActive(item.href))
                      ? 'bg-accent-muted text-accent'
                      : 'text-foreground-secondary hover:text-foreground hover:bg-cream/5'
                  }`}
                >
                  More
                  <svg className={`w-3.5 h-3.5 transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {moreMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setMoreMenuOpen(false)}
                    />
                    <div className="absolute top-full right-0 mt-1 w-48 bg-background border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                      <div className="py-1">
                        {MORE_NAV_ITEMS.map(item => (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={() => setMoreMenuOpen(false)}
                            className={`block px-4 py-2 text-sm transition-colors ${
                              isActive(item.href)
                                ? 'bg-accent-muted text-accent'
                                : 'text-foreground-secondary hover:text-foreground hover:bg-cream/5'
                            }`}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground-tertiary hover:text-foreground hover:border-border/30 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden sm:inline text-sm">Search</span>
                <kbd className="hidden sm:inline px-1.5 py-0.5 bg-cream/10 text-foreground-muted text-xs rounded ml-2">
                  ⌘K
                </kbd>
              </button>

              {/* Notifications Button */}
              <button
                onClick={() => setNotificationsOpen(true)}
                className="relative p-2 text-foreground-tertiary hover:text-foreground transition-colors"
                title="Notifications"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Back to Site */}
              <Link
                href="/"
                className="p-2 text-foreground-tertiary hover:text-foreground transition-colors"
                title="Back to site"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Notifications Panel */}
      <NotificationsPanel
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        entities={entities}
        assessments={assessments}
        onNotificationCount={handleNotificationCount}
      />
    </>
  );
}
